import { debugLog, debugError } from '@utils';

// Imported by path rather than re-exported from the `@utils` barrel ON PURPOSE.
// Theme assets are served with `max-age=31557600`, and rollup's cross-chunk
// imports resolve to the UNVERSIONED `assets/utils.js` — so adding an export to
// that shared chunk pairs a freshly-fetched `campaigns-and-channels.js` (its
// <script src> carries `?v=`) with a year-old cached `utils.js`, and the module
// fails to link: "does not provide an export named 'isExpired'". That kills
// campaigns and channels outright for returning visitors. Imported here it is
// inlined into this entry, which is versioned. NGE-10856.
import { isExpired, resolveChannel } from './utils/channelAttribution/index.mjs';

class CampaignsAndChannels {
  static debug = false;
  static #dateNow = new Date().getTime();

  // After 15 minutes in the current session, update the campaign and channel data. To make sure we have the latest data.
  static #updateTime = 1000 * 60 * 15;

  static #urlParams = {
    campaign: 'cmp',
    channel: 'ch',
    clear: 'cmp-clear',
  };

  static #apiVersion = '2025-07';
  static #queryCampaign = (handle, scope) => `
    query GetCampaign {
      metaobject(handle: {handle: "${handle}", type: "${scope}cm_campaign"}) {
        id
        handle
        title: field(key: "title") {
          value
        }
        start_time: field(key: "start_time") {
          value
        }
        end_time: field(key: "end_time") {
          value
        }
        active: field(key: "active") {
          value
        }
        channels: field(key: "channels") {
          value
        }
        market_groups: field(key: "market_groups") {
          value
        }
      }
    }
  `;

  constructor() {
    // Check if we have a cart section
    CampaignsAndChannels.debug && debugLog('Campaigns: Initial data', window.theme?.campaign);
    CampaignsAndChannels.debug && debugLog('Channels: Initial data', window.theme?.channel);

    // State
    this.isValid = null;
    this.isOutdated = null;
    this.newCampaign = null;
    this.newChannel = null;
    this.campaignData = null;
    this.channelData = null;
    this.cartAttributes = null;
    this.inactivityTime = -1;

    if (!window.theme.app_id || !window.theme.app_scope) {
      CampaignsAndChannels.debug &&
        debugLog('Campaigns & Channels: All app id or scope for the campaign app is required');
      return;
    }

    // Inactivity time
    const expiration_time = parseInt(window.theme.channel.expiration_time);
    if (expiration_time) {
      this.inactivityTime = 1000 * 60 * expiration_time;
    }

    // Price elements
    this.priceElements = document.querySelectorAll('[data-prices]');
    this.lineItems = document.querySelector('line-items');
    this.cartSection = document.querySelector('[data-cart-section]');
    this.cartSummary = document.querySelector('cart-summary');
    this.debugElement = document.querySelector('[data-debug-channel]');

    // Test set channel button
    document.querySelectorAll('[data-set-channel]').forEach((button) => {
      button.addEventListener('click', () => {
        const channel = button.dataset.setChannel;
        const url = new URL(window.location);
        Object.values(CampaignsAndChannels.#urlParams).forEach((param) => {
          url.searchParams.delete(param);
        });
        url.searchParams.set(CampaignsAndChannels.#urlParams.channel, channel);
        window.history.replaceState({}, '', url);
      });
    });

    // Test clear campaign button
    document.querySelectorAll('[data-clear-campaign]').forEach((button) => {
      button.addEventListener('click', () => {
        const url = new URL(window.location);
        Object.values(CampaignsAndChannels.#urlParams).forEach((param) => {
          url.searchParams.delete(param);
        });
        url.searchParams.set(CampaignsAndChannels.#urlParams.clear, 'true');
        window.history.replaceState({}, '', url);
      });
    });

    // Init the campaign and channel
    this.init();
  }

  /**
   * Initialize the campaign tracking
   */
  async init() {
    // Check if we need to init a new campaign
    this.newCampaign = this.#isNewCampaign();
    CampaignsAndChannels.debug &&
      this.newCampaign &&
      debugLog('Campaign: New campaign', this.newCampaign);

    // Check if we need to init a new channel
    this.newChannel = this.#isNewChannel();
    CampaignsAndChannels.debug &&
      this.newChannel &&
      debugLog('Channel: New channel', this.newChannel);

    // Always pass raw URL params to cart attributes (for debugging/BI)
    // Must await to prevent race condition with cmp-clear (ensures params are cleared before campaign data is fetched)
    await this.#setRawUrlParams();

    // Get the campaign data from the store or local storage
    this.campaignData = await this.#getCampaignData();
    CampaignsAndChannels.debug &&
      this.campaignData &&
      debugLog('Campaign: Campaign data', this.campaignData);

    // Get the channel data from the store or local storage
    this.channelData = await this.#getChannelData();
    CampaignsAndChannels.debug &&
      this.channelData &&
      debugLog('Channel: Channel data', this.channelData);

    // Validate the campaign data
    this.isValidCampaign = this.#validateCampaign();
    this.isValidChannel = this.#validateChannel();

    // Clear the campaign and/or channel storage if it's not valid
    if (!this.isValidCampaign || !this.isValidChannel) {
      CampaignsAndChannels.debug &&
        !this.isValidCampaign &&
        debugLog('Campaign: Clear invalid campaign');
      CampaignsAndChannels.debug &&
        !this.isValidChannel &&
        debugLog('Channel: Clear invalid channel');
      this.#clearData(!this.isValidCampaign, !this.isValidChannel);
      this.#clearLocalStorage(!this.isValidCampaign, !this.isValidChannel);
      await this.#clearCartAttributes(!this.isValidCampaign, !this.isValidChannel);
    }

    // Format the campaign data
    const formattedData = this.#formatData();

    // Set the new campaign data because it's a new campaign
    if (this.newCampaign || this.newChannel) {
      CampaignsAndChannels.debug &&
        this.newCampaign &&
        debugLog('Campaign: Set new campaign', formattedData.campaign);
      CampaignsAndChannels.debug &&
        this.newChannel &&
        debugLog('Channel: Set new channel', formattedData.channel);
      this.#setLocalStorage(formattedData);
      await this.#setCartAttributes(formattedData);
      this.#tracking(formattedData);
    }

    // Update the campaign data if it's outdated
    else if (this.isOutdated && (this.isValidCampaign || this.isValidChannel)) {
      CampaignsAndChannels.debug &&
        this.isValidCampaign &&
        debugLog('Campaign: Update campaign data', formattedData);
      CampaignsAndChannels.debug &&
        this.isValidChannel &&
        debugLog('Channel: Update channel data', formattedData);
      this.#setLocalStorage(formattedData);
      await this.#setCartAttributes(formattedData);
    }

    // Display the prices always
    this.#displayPrices(formattedData?.campaign?.handle, formattedData?.channel?.handle);
  }

  /**
   * Check if we need to init a new campaign based on the campaign params in the url
   * @returns {string|null} The campaign handle
   */
  #isNewCampaign() {
    // Get the campaign from the url params
    const params = new URLSearchParams(window.location.search);

    // If we are on the campaign page, use the campaign data from the path, otherwise use the url param
    const campaign = window.campaignData
      ? window.location.pathname.split('/').pop()
      : params.get(CampaignsAndChannels.#urlParams.campaign);

    // If we don't have a campaign or channel, return false
    if (!campaign) {
      return null;
    }

    // Return true if we have a campaign and channel
    return campaign.toLowerCase();
  }

  /**
   * Check if we need to init a new channel based on the channel params in the url
   * @returns {string|null} The channel handle
   */
  #isNewChannel() {
    // Get the channel from the url params
    const params = new URLSearchParams(window.location.search);
    let channel = params.get(CampaignsAndChannels.#urlParams.channel);

    // No ?ch= param — a paid-landing template may still imply one (NGE-10852)
    if (!channel) {
      channel = this.#templateChannel();
    }

    // If the channel is the default channel, use '_'. AFTER the template
    // fallback, deliberately: channel-settle.liquid runs the template channel
    // through mapChannel() (default → '_'), so this half must apply the same
    // rule to the same input or the two halves disagree on a shop whose default
    // handle appears in the app index — the fight mode both files warn about.
    // Ordering pinned by tests/channel-settle-parity.test.mjs. (davis-abols, #187)
    if (channel && channel.toLowerCase() === window.theme.channel.default) {
      channel = '_';
    }

    // If we don't have a channel, return false
    if (!channel) {
      return null;
    }

    // Convert the channel to lowercase
    channel = channel.toLowerCase();

    // Return true if we have a channel
    return channel;
  }

  /**
   * NGE-10852: a paid-landing template implies its channel
   * (window.theme.channel.template, derived server-side from template.suffix).
   * Adopt it ONLY when no explicit ?ch= param decided otherwise and no LIVE
   * stored channel exists — the same precedence channel-settle.liquid applies
   * pre-paint; if the two halves disagree they fight and every pageview
   * reloads. A live stored channel wins so a crm-attributed visitor browsing a
   * sea template keeps seeing (and being charged) crm prices.
   * @returns {string|null} The template's channel handle, or null
   */
  #templateChannel() {
    const templateChannel = (window.theme.channel.template || '').toLowerCase();
    if (!templateChannel) {
      return null;
    }
    const stored = this.#getLocalStorage('channel');
    const storedLive =
      stored?.handle &&
      !isExpired({
        timestamp: stored.timestamp,
        inactivityMs: this.inactivityTime,
        now: CampaignsAndChannels.#dateNow,
      });
    return storedLive ? null : templateChannel;
  }

  /**
   * Validate if the campaign data is valid
   * @returns {boolean}
   */
  #validateCampaign() {
    // If we need to clear the campaign, then return false
    // Read as a real param, not a substring (@davis-abols, PR #150 — NGE-10856).
    // `search.includes('cmp-clear')` also matches a URL that merely CONTAINS the string —
    // `?promo=cmp-clear-b` would clear both campaign and channel. The pre-paint settle in
    // `channel-settle.liquid` uses `params.has()`, so the two halves disagreed on such a
    // URL: the snippet resolved the stored channel and POSTed it, this half then treated
    // everything as invalid and cleared it again — the "two halves fight" mode, one
    // redirect per pageview. `has()` is already the in-file precedent (see "Clear raw
    // params" below), so these two were the outliers.
    if (new URLSearchParams(window.location.search).has(CampaignsAndChannels.#urlParams.clear)) {
      return false;
    }

    // If we don't have a campaign
    if (!this.campaignData) {
      // And no campaign in the cart attributes, then that's valid because no campaign should be cleared
      if (!window.theme.campaign.handle) {
        return true;
      }
      return false;
    }

    // Check if the campaign is active
    if (!this.campaignData.active) return false;

    // Do we have a campaign?
    if (!this.campaignData.handle) return false;

    // TODOS: Are channels required on campaign or discount level?
    // Do we have a channel tiers?
    // if (!this.campaignData.channels) return false;

    // Validate if the current channel exists in the campaign data
    if (this.campaignData.channels && this.campaignData.channels.length > 0) {
      const channel = this.channelData?.handle || window.theme.channel.default;
      if (channel && this.campaignData.channels && !this.campaignData.channels.includes(channel)) {
        return false;
      }
    }

    // Has a valid start and end date?
    const startDate = new Date(this.campaignData.start_time)?.getTime() ?? null;
    const endDate = new Date(this.campaignData.end_time)?.getTime() ?? null;
    if (!startDate || CampaignsAndChannels.#dateNow < startDate) return false;
    if (endDate && CampaignsAndChannels.#dateNow > endDate) return false;

    // Validate the inactivity time, measured from when the campaign was ACQUIRED
    if (
      isExpired({
        timestamp: this.campaignData.timestamp,
        inactivityMs: this.inactivityTime,
        now: CampaignsAndChannels.#dateNow,
      })
    ) {
      CampaignsAndChannels.debug && debugLog('Campaign: Expired');
      return false;
    }
    CampaignsAndChannels.debug && debugLog('Campaign: Not expired');

    return true;
  }

  /**
   * Validate if the channel data is valid
   * @returns {boolean}
   */
  #validateChannel() {
    // If we need to clear the channel, then return false
    // Same substring-vs-param fix as #validateCampaign (NGE-10856).
    if (new URLSearchParams(window.location.search).has(CampaignsAndChannels.#urlParams.clear)) {
      return false;
    }

    // If we don't have a channel
    if (!this.channelData) {
      // And no channel in the cart attributes, then that's valid because no channel should be cleared
      if (!window.theme.channel.handle) {
        return true;
      }
      return false;
    }

    // Do we have a channel?
    if (!this.channelData.handle) return false;

    // Validate the inactivity time, measured from when the channel was ACQUIRED
    // from a `?ch=` tag. resolveChannel() already drops an expired stored channel,
    // so this is the guard for a channel that expired mid-resolution.
    if (
      isExpired({
        timestamp: this.channelData.timestamp,
        inactivityMs: this.inactivityTime,
        now: CampaignsAndChannels.#dateNow,
      })
    ) {
      CampaignsAndChannels.debug && debugLog('Channel: Expired');
      return false;
    }
    CampaignsAndChannels.debug && debugLog('Channel: Not expired');

    return true;
  }

  /**
   * Check if the campaign data is outdated
   * @param {string} type - The type of data to check (campaign or channel)
   * @param {Object} data - The data to check
   * @returns {boolean}
   */
  #isOutdated(type, data) {
    // If we don't have data, then return false
    if (!data) {
      return false;
    }

    // Cache freshness, never attribution age. `syncedAt` was introduced by
    // NGE-10856; entries written by the previous version only carry `timestamp`,
    // so fall back to it — the first re-sync rewrites them with both.
    const syncedAt = data.syncedAt || data.timestamp;

    // We have local storage data but no campaign or channel data in the cart attributes
    if (syncedAt && !window.theme[type].handle) {
      return true;
    }

    // Validate the if the campaign data should be updated with latest data
    if (syncedAt) {
      const lastUpdate = new Date(syncedAt)?.getTime();
      if (lastUpdate) {
        return CampaignsAndChannels.#dateNow - lastUpdate > CampaignsAndChannels.#updateTime;
      }
    }

    // If we couldn't validate we assume it's should be updated with latest data
    return true;
  }

  /**
   * Get the campaign data from the store or local storage
   * @returns {Object}
   */
  async #getCampaignData() {
    // Get the campaign data from the window object (only on the campaign page)
    let campaignData = null;

    // If we are initializing a new campaign
    if (this.newCampaign) {
      // Get the campaign data from the window object (only on the campaign page) or fetch the latest data from the store
      campaignData = window.campaignData || (await this.#fetchCampaignData(this.newCampaign));
    }

    // Else get the campaign data from the local storage
    else {
      campaignData = this.#getLocalStorage('campaign');

      // Check if the channel data is outdated
      if (this.#isOutdated('campaign', campaignData)) {
        campaignData = {
          ...campaignData, // Make sure we keep the previous timestamp
          ...(await this.#fetchCampaignData(campaignData.handle)),
        };
        this.isOutdated = true;
      }
    }

    // If we couldn't get the campaign data, return null
    if (!campaignData) {
      return null;
    }

    // Format the campaign data
    if (campaignData.handle) {
      campaignData.handle = campaignData.handle.toLowerCase();
    }
    if (campaignData.channels) {
      campaignData.channels = campaignData.channels.map((channel) => channel.toLowerCase());
    }
    if (campaignData.market_groups) {
      campaignData.market_groups = campaignData.market_groups.map((market) => market.toUpperCase());
    }

    // Return the campaign data
    return campaignData;
  }

  /**
   * Get the channel data from the store or local storage
   * @returns {Object}
   */
  #getChannelData() {
    const stored = this.#getLocalStorage('channel');

    // A stored channel still has to be re-synced into the cart attributes when the
    // cart lost them (new cart cookie) or when the cached copy is stale — that is
    // what drives the `isOutdated` branch in init(). Freshness is tracked by
    // `syncedAt`, deliberately NOT by the attribution `timestamp`: the two used to
    // be the same field, which is why every re-sync silently renewed the
    // attribution window (fault 2, NGE-10856).
    if (!this.newChannel && this.#isOutdated('channel', stored)) {
      this.isOutdated = true;
    }

    const channelData = resolveChannel({
      param: this.newChannel,
      stored,
      index: window.theme.channel.index,
      defaultChannel: window.theme.channel.default,
      now: CampaignsAndChannels.#dateNow,
      inactivityMs: this.inactivityTime,
    });

    // The stored attribution outlived its window. Drop it here so it cannot sit in
    // local storage forever waiting to be resurrected into a fresh cart.
    if (!channelData && stored) {
      CampaignsAndChannels.debug && debugLog('Channel: Attribution expired, clearing');
      this.#clearLocalStorage(false, true);
    }

    return channelData;
  }

  /**
   * Fetch the campaign data from the store
   * @param {string} handle - The campaign metaobject entry handle
   * @returns {Object}
   */
  async #fetchCampaignData(handle) {
    try {
      const response = await fetch(`/api/${CampaignsAndChannels.#apiVersion}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.theme.storefront_api_token,
        },
        body: JSON.stringify({
          query: CampaignsAndChannels.#queryCampaign(handle, window.theme.app_scope),
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.data.metaobject) {
        throw new Error('Failed to fetch campaign data');
      }

      // Format the campaign data
      const campaign = {
        title: data.data.metaobject?.title?.value,
        handle: data.data.metaobject?.handle,
        start_time: data.data.metaobject?.start_time?.value,
        end_time: data.data.metaobject?.end_time?.value,
        active: data.data.metaobject?.active?.value === 'true',
        channels: JSON.parse(data.data.metaobject?.channels?.value || '[]'),
        market_groups: JSON.parse(data.data.metaobject?.market_groups?.value || '[]'),
      };

      return campaign;
    } catch {
      CampaignsAndChannels.debug && debugLog('Campaign: Campaign data not found');
      return null;
    }
  }

  /**
   * Format the campaign data
   * @param {Object} data - The campaign data
   * @returns {Object}
   */
  #formatData() {
    const data = {};

    // `timestamp` is the ATTRIBUTION clock — stamped when a campaign or channel is
    // acquired from a URL tag, and carried unchanged after that. `syncedAt` is the
    // cache clock — stamped on every write, and the only thing #isOutdated() reads.
    //
    // They used to be one field, re-stamped on every write. Because an untagged
    // pageview writes too (the isOutdated re-sync), every pageview pushed the
    // expiry out by another full window: a visitor who once clicked a CRM link
    // kept the CRM tier for as long as they kept browsing, and no plain URL ever
    // released it. That is fault 2 of NGE-10856.
    const now = CampaignsAndChannels.#dateNow;

    if (this.campaignData) {
      data.campaign = {
        ...this.campaignData,
        handle: this.newCampaign || this.campaignData?.handle,
        timestamp: this.newCampaign ? now : this.campaignData.timestamp || now,
        syncedAt: now,
      };
    }
    if (this.channelData) {
      data.channel = {
        ...this.channelData,
        timestamp: this.channelData.timestamp || now,
        syncedAt: now,
      };
      // Tier 0 is the default price list, not "no tier" — see resolveTier().
      data.tier = this.channelData.index;
    }
    return data;
  }

  /**
   * Clear the campaign and/or channel data
   * @param {boolean} campaign - Clear the campaign data
   * @param {boolean} channel - Clear the channel data
   */
  #clearData(campaign = true, channel = true) {
    if (campaign) {
      this.newCampaign = null;
      this.campaignData = null;
    }
    if (channel) {
      this.newChannel = null;
      this.channelData = null;
    }
  }

  /**
   * Get the local storage
   * @returns {Object}
   * @param {string} key - The type of data to get (campaign or channel)
   */
  #getLocalStorage(key = 'campaign') {
    if (!key) {
      return null;
    }
    const storage = JSON.parse(localStorage.getItem(key)) || null;
    return storage;
  }

  /**
   * Set the local storage with the campaign data
   * @param {Object} data - The formatted campaign data

   */
  #setLocalStorage(data) {
    if (!data) {
      return;
    }
    if (data.campaign?.handle) {
      localStorage.setItem('campaign', JSON.stringify(data.campaign));
      window.theme.campaign.handle = data.campaign?.handle;
    }
    if (data.channel?.handle) {
      localStorage.setItem('channel', JSON.stringify(data.channel));
      window.theme.channel.handle = data.channel?.handle;
    }
    if (data.tier !== undefined) {
      localStorage.setItem('tier', JSON.stringify(data.tier));
      window.theme.tier = data.tier;
    }
  }

  /**
   * Clear the campaign data
   * @param {boolean} campaign - Clear the campaign data
   * @param {boolean} channel - Clear the channel data
   */
  #clearLocalStorage(campaign = true, channel = true) {
    if (campaign) {
      localStorage.removeItem('campaign');
    }
    if (channel) {
      localStorage.removeItem('channel');
      localStorage.removeItem('tier');
    }
  }

  /**
   * Set the cart attributes with the campaign and channel data
   */
  async #setCartAttributes(data) {
    if (!data) {
      return;
    }
    try {
      const attributes = {
        _market: window.theme.market || '',
        _brand_id: window.theme.brandId || '',
        _language: window.theme.language || '',
        _market_groups: window.theme.market_group.index,
      };

      if (data.campaign?.handle) {
        attributes._campaign = data.campaign.handle;
      }
      if (data.channel?.handle) {
        attributes._channel = data.channel.handle;
      }
      if (data.tier !== undefined) {
        attributes._tier = data.tier;
      }

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update cart attributes');
      }

      return await response.json();
    } catch (error) {
      debugError('Error setting cart attributes:', error);
      return null;
    }
  }

  /**
   * Clear the cart attributes
   * @param {boolean} campaign - Clear the campaign data
   * @param {boolean} channel - Clear the channel data
   */
  async #clearCartAttributes(campaign = true, channel = true) {
    try {
      const data = {};
      if (campaign) {
        data._campaign = null;
      }
      if (channel) {
        data._channel = null;
        data._tier = null;
      }

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            ...data,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update cart attributes');
      }

      return await response.json();
    } catch (error) {
      debugError('Error setting cart attributes:', error);
      return null;
    }
  }

  /**
   * Set raw URL parameters as cart attributes for debugging/BI,
   * independent of campaign validation. Only sets when params are present in URL.
   */
  async #setRawUrlParams() {
    const params = new URLSearchParams(window.location.search);

    // Clear raw params when cmp-clear is requested
    if (params.has(CampaignsAndChannels.#urlParams.clear)) {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { _cmp_param: '', _ch_param: '' } }),
        });
        CampaignsAndChannels.debug && debugLog('Raw URL params cleared');
      } catch (error) {
        debugError('Error clearing raw URL params:', error);
      }
      return;
    }

    const cmp = params.get(CampaignsAndChannels.#urlParams.campaign);
    const ch = params.get(CampaignsAndChannels.#urlParams.channel);

    if (!cmp && !ch) return;

    const attributes = {};
    if (cmp) attributes._cmp_param = cmp.toLowerCase();
    if (ch) attributes._ch_param = ch.toLowerCase();

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
      });
      CampaignsAndChannels.debug && debugLog('Raw URL params set:', attributes);
    } catch (error) {
      debugError('Error setting raw URL params:', error);
    }
  }

  /**
   * Display the prices on the campaign page if the channel is loaded
   * @param {String} channel - The channel to display the prices for
   * @param {Array} channels - The available channels
   */
  async #displayPrices(campaignHandle = null, channelHandle = window.theme.channel.default) {
    let fetchNewPrices = false;

    // If the campaign changed (including going from no campaign to a campaign), fetch new prices
    if (campaignHandle !== (window.theme.campaign.initial || null)) {
      fetchNewPrices = true;
    }

    // If the initial channel is not the same as the new channel, then fetch new prices
    if (channelHandle !== window.theme.channel.initial) {
      fetchNewPrices = true;
    }

    // Fetch new prices
    if (fetchNewPrices) {
      CampaignsAndChannels.debug && debugLog('Campaigns & Channels: Fetch new prices');
      await this.#updatePrices();
      // Re-query price elements because #updatePrices() replaces innerHTML,
      // which creates new child [data-prices] elements (e.g. dynamic-price-text)
      // that still have the 'loading' class. The old references are now detached.
      this.priceElements = document.querySelectorAll('[data-prices]');
    } else {
      // No swap — publish the server-rendered prices as-is (NGE-10852).
      // The swap path publishes from the fetched document inside #updatePrices().
      this.#publishResolvedPrices();
    }

    // Display the price elements
    this.priceElements.forEach((element) => {
      element.classList.remove('loading');
    });

    // Display the cart section
    if (this.cartSection) {
      this.cartSection.classList.remove('loading');
    }

    // Display the channel handle in the debug element
    if (channelHandle && this.debugElement) {
      this.debugElement.textContent =
        channelHandle === '_' ? window.theme.channel.default : channelHandle;
    }

    CampaignsAndChannels.debug && debugLog('Campaigns & Channels: Display prices');
  }

  /**
   * Fetch the new prices after the channel is updated and update the price elements
   *    * @param {String} channel - The channel to display the prices for
   * @param {Array} channels - The available channels
   */
  async #updatePrices() {
    try {
      const url = new URL(window.location.href);
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'text/html',
        },
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch current page');
      }

      // Get the data
      const data = await response.text();

      // Parse the data
      const parser = new DOMParser();
      const html = parser.parseFromString(data, 'text/html');

      // Get the price elements — only top-level ones (skip nested data-prices
      // inside other data-prices wrappers, since replacing the parent innerHTML
      // already includes updated children)
      const allPriceElements = html.querySelectorAll('[data-prices]');
      const topLevelPriceElements = [];
      for (const el of allPriceElements) {
        if (!el.parentElement?.closest('[data-prices]')) {
          topLevelPriceElements.push(el);
        }
      }

      // Update the price elements (without dependencies like pubSub)
      for (const element of topLevelPriceElements) {
        const id = element.dataset.prices;
        const priceHtml = element.innerHTML;
        for (const priceElement of this.priceElements) {
          if (priceElement.dataset.prices === id) {
            priceElement.innerHTML = priceHtml;
          }
        }
      }

      // Update the line items (without dependencies like pubSub)
      if (html.querySelector('line-items') && this.lineItems) {
        this.lineItems.innerHTML = html.querySelector('line-items').innerHTML;
      }

      // Update the cart summary (without dependencies like pubSub)
      if (html.querySelector('cart-summary') && this.cartSummary) {
        this.cartSummary.innerHTML = html.querySelector('cart-summary').innerHTML;
      }

      // Publish the re-resolved prices from the fresh document (NGE-10852)
      this.#publishResolvedPrices(html);

      return data;
    } catch (error) {
      debugError('Error setting cart attributes:', error);
      return null;
    }
  }

  /**
   * NGE-10852 / NGE-11533: put the channel-resolved prices on the analytics bus.
   * The native product_viewed web-pixel event carries the render-time BASE
   * variant price, so on channel-tagged pages GA4's view_item reports a price
   * the visitor never saw. Web pixels cannot read page storage or the DOM, but
   * they CAN receive custom events — so the theme publishes the server-resolved
   * prices (rendered into [data-channel-prices] by channel-prices-json.liquid)
   * as `tcg_channel_price_resolved`, and the custom pixel merges them into its
   * GA4 items. Called once per pageview, and again after a price swap with the
   * freshly fetched document so the payload matches what is on screen.
   * @param {Document} doc - The document to read the prices tag from
   */
  #publishResolvedPrices(doc = document) {
    try {
      const tag = doc.querySelector('[data-channel-prices]');
      if (!tag) {
        return;
      }
      // After a swap, sync the live tag so later readers see current data
      if (doc !== document) {
        const live = document.querySelector('[data-channel-prices]');
        if (live) {
          live.textContent = tag.textContent;
        }
      }
      const payload = JSON.parse(tag.textContent);
      // Prefer the tag's own tier — it is resolved in the same document as the
      // prices, so it stays honest after a swap. window.theme.tier is render-time
      // only and would label fresh prices with the previous request's tier.
      if (!Number.isInteger(payload.tier)) {
        payload.tier = parseInt(window.theme.tier, 10) || 0;
      }
      window.Shopify?.analytics?.publish?.('tcg_channel_price_resolved', payload);
    } catch (error) {
      debugError('Error publishing resolved prices:', error);
    }
  }

  /**
   * Track the campaign
   */
  #tracking(data) {
    if (window.analytics) {
      if (this.newCampaign) {
        window.analytics.track('New Campaign', {
          ...data.campaign,
        });
      }
      if (this.newChannel) {
        window.analytics.track('New Channel', {
          ...data.channel,
        });
      }
    }
  }
}

// Initialize campaign tracking
new CampaignsAndChannels();
